import React, { useState, useEffect, Suspense, lazy, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import './App.css';
import { ThemeProvider } from './context/ThemeContext';
import { LanguageProvider, useLanguage } from './context/LanguageContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { BACKEND_URL } from './services/config';
import { checkAndRefreshToken, isTokenExpired } from './services/auth';
import Header from './components/Header';
import PreActivation from './components/PreActivation';
import WhatsAppFloat from './components/WhatsAppFloat';
import BottomNav from './components/BottomNav';
import MarketingAngle from './components/MarketingAngle';
import AuthPage from './components/AuthPage';
import { ToastProvider } from './components/Toast';
import { ListSkeleton, StatsSkeleton } from './components/Skeleton';
import { LoadingWithTimeout } from './components/LoadingStates';
import LiveSupport from './components/LiveSupport';
import SystemGuideModal from './components/SystemGuideModal';
import LivePriceTicker from './components/LivePriceTicker';
import MarketSessionBanner from './components/MarketSessionBanner';
import { getUserStatus, activateUser, getExchangeRate, trackPageVisit, getGuideProgress, saveGuideProgress } from './services/api';
import { Calculator, GraduationCap, CheckSquare, Globe, Crown, LogOut, RefreshCw } from 'lucide-react';

// v7.6: Lazy load ALL tab components for faster initial load
// Only loads when user switches to that tab
const AdminPanel = lazy(() => import('./components/AdminPanel'));
const PremiumFeatures = lazy(() => import('./components/PremiumFeatures'));
const RiskCalculator = lazy(() => import('./components/RiskCalculator'));
const MarketSnapshot = lazy(() => import('./components/MarketSnapshot'));
const DrawdownAlert = lazy(() => import('./components/DrawdownAlert'));
const CompoundingCalculator = lazy(() => import('./components/CompoundingCalculator'));
const BreakEvenCalculator = lazy(() => import('./components/BreakEvenCalculator'));
const EconomicCalendar = lazy(() => import('./components/EconomicCalendar'));
const TargetTracker = lazy(() => import('./components/TargetTracker'));
const TradingEducation = lazy(() => import('./components/TradingEducation'));
const PipValueCalculator = lazy(() => import('./components/PipValueCalculator'));
const DailyChecklist = lazy(() => import('./components/DailyChecklist'));

// Loading fallback component for lazy loaded components
const LazyLoadFallback = () => (
  <LoadingWithTimeout 
    isLoading={true} 
    loadingText="Memuatkan modul..."
    slowText="Modul agak besar, sila tunggu..."
  >
    <div />
  </LoadingWithTimeout>
);

import ErrorBoundary from './components/ErrorBoundary';

// v7.6: Lightweight tab loading indicator
const TabFallback = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '40px', opacity: 0.6 }}>
    <div style={{ width: '24px', height: '24px', border: '3px solid var(--border-subtle)', borderTopColor: 'var(--accent-primary)', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
  </div>
);

function AppContent() {
  const { isAuthenticated, loading: authLoading, user, logout } = useAuth();
  const [isActivated, setIsActivated] = useState(null);
  const [exchangeRate, setExchangeRate] = useState(4.47);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(() => {
    // Valid tabs that have rendering in App.js
    const validTabs = ['checklist', 'tools', 'premium', 'education'];
    
    // Restore activeTab from localStorage if available (for language switch)
    const savedTab = localStorage.getItem('fira-active-tab');
    if (savedTab) {
      localStorage.removeItem('fira-active-tab'); // Clear after use
      if (validTabs.includes(savedTab)) {
        return savedTab;
      }
      console.warn(`[App] Invalid saved tab "${savedTab}", falling back to premium`);
    }
    // Check if guide is already complete - go to premium instead
    const guideAlreadyComplete = localStorage.getItem('fira-quiz-complete') === 'true';
    return 'premium';
  });
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  // Force dark mode always
  useEffect(() => {
    document.documentElement.classList.add('dark-mode');
    localStorage.setItem('fira-theme', 'dark');
  }, []);
  const { t, language } = useLanguage();
  
  // Admin panel state - 7 clicks on logo to access (whitelist protected)
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [logoClickCount, setLogoClickCount] = useState(0);
  const [lastClickTime, setLastClickTime] = useState(0);
  const [adminWhitelistChecked, setAdminWhitelistChecked] = useState(false);
  
  // Page tracking for BI
  const [pageStartTime, setPageStartTime] = useState(Date.now());

  // Guide completion state - check localStorage first, then sync with backend
  const [guideComplete, setGuideComplete] = useState(() => {
    return localStorage.getItem('fira-quiz-complete') === 'true';
  });
  const [showGuideReminder, setShowGuideReminder] = useState(false);
  const [pendingTab, setPendingTab] = useState(null);
  
  // Refresh key - increments when tab changes to force data refresh
  const [refreshKey, setRefreshKey] = useState(0);

  // Fetch guide progress from backend when authenticated
  useEffect(() => {
    if (!isAuthenticated) return;
    
    const fetchGuideProgress = async () => {
      try {
        const progress = await getGuideProgress();
        if (progress.quiz_complete) {
          setGuideComplete(true);
          localStorage.setItem('fira-quiz-complete', 'true');
        }
      } catch (error) {
        console.error('Error fetching guide progress:', error);
      }
    };
    
    fetchGuideProgress();
  }, [isAuthenticated]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Increment refreshKey when tab changes to force data refresh
  useEffect(() => {
    setRefreshKey(prev => prev + 1);
  }, [activeTab]);

  // Listen for LiveSupport tab navigation events
  useEffect(() => {
    const handleNavigateToTab = (e) => {
      setActiveTab(e.detail);
      setSidebarOpen(false);
    };
    
    window.addEventListener('navigateToTab', handleNavigateToTab);
    
    return () => {
      window.removeEventListener('navigateToTab', handleNavigateToTab);
    };
  }, []);

  // Token refresh on app resume/visibility change
  useEffect(() => {
    if (!isAuthenticated) return;
    
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible') {
        console.log('[APP] App became visible, checking token...');
        
        // Check if token is expired
        if (isTokenExpired()) {
          console.log('[APP] Token expired, logging out...');
          logout();
          return;
        }
        
        // Try to refresh token if expiring soon
        const tokenValid = await checkAndRefreshToken();
        if (!tokenValid) {
          console.log('[APP] Token refresh failed, logging out...');
          logout();
        }
      }
    };
    
    // Check token on mount
    handleVisibilityChange();
    
    // Listen for visibility changes
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Also check periodically (every 5 minutes)
    const intervalId = setInterval(handleVisibilityChange, 5 * 60 * 1000);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      clearInterval(intervalId);
    };
  }, [isAuthenticated, logout]);

  useEffect(() => {
    // Only fetch data if authenticated
    if (!isAuthenticated) return;
    
    const fetchInitialData = async () => {
      // Quick start - show app immediately, then load data
      setLoading(false);
      setIsActivated(true);
      setExchangeRate(4.47);
      
      // Load ALL data in parallel for faster startup
      try {
        const [userStatus, rateData] = await Promise.all([
          getUserStatus().catch(() => ({ is_activated: true })),
          getExchangeRate().catch(() => ({ usd_to_myr: 4.47 }))
        ]);
        
        setIsActivated(userStatus.is_activated);
        setExchangeRate(rateData.usd_to_myr);
      } catch (error) {
        console.error('Error fetching initial data:', error);
      }
    };
    fetchInitialData();
  }, [isAuthenticated]);

  // Redirect to login if not authenticated (MUST be after all hooks)
  if (!authLoading && !isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  // Show loading while checking auth
  if (authLoading) {
    return <LazyLoadFallback />;
  }

  const handleActivate = async () => {
    try {
      await activateUser('DEMO-123456');
      setIsActivated(true);
    } catch (error) {
      console.error('Error activating user:', error);
      setIsActivated(true);
    }
  };

  const handleGuideComplete = async () => {
    setGuideComplete(true);
    localStorage.setItem('fira-quiz-complete', 'true');
    
    // Save to backend so it persists across devices/sessions
    try {
      await saveGuideProgress({
        completed_sections: JSON.parse(localStorage.getItem('fira-guide-progress') || '[]'),
        quiz_complete: true
      });
    } catch (error) {
      console.error('Error saving guide progress:', error);
    }
  };

  const handleTabChange = (tabId) => {
    // Valid tabs that have rendering in App.js
    const validTabs = ['checklist', 'tools', 'premium', 'education'];
    
    // Validate tab before changing - fallback to premium if invalid
    if (!validTabs.includes(tabId)) {
      console.warn(`[App] Invalid tab "${tabId}", falling back to premium`);
      tabId = 'premium';
    }
    
    // Track page visit duration for BI
    const duration = Math.floor((Date.now() - pageStartTime) / 1000);
    if (activeTab && duration > 0) {
      trackPageVisit(activeTab, duration);
    }
    setPageStartTime(Date.now());
    
    setActiveTab(tabId);
    setSidebarOpen(false);
  };
  
  // Handle logo click for hidden admin panel (whitelist protected)
  const handleLogoClick = async () => {
    const now = Date.now();
    // Reset count if more than 2 seconds since last click
    if (now - lastClickTime > 2000) {
      setLogoClickCount(1);
    } else {
      setLogoClickCount(prev => prev + 1);
    }
    setLastClickTime(now);
    
    // Open admin panel after 7 clicks - but only if whitelisted
    if (logoClickCount >= 6) {
      setLogoClickCount(0);
      
      // Check whitelist silently - no error shown if not allowed
      try {
        const token = localStorage.getItem('fira_auth_token');
        if (!token) return; // Silent fail - not logged in
        
        const API_URL = BACKEND_URL;
        const response = await fetch(`${API_URL}/api/admin/whitelist/check`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await response.json();
        
        if (data.allowed) {
          setShowAdminPanel(true);
          setAdminWhitelistChecked(true);
        }
        // Silent fail if not allowed - nothing happens
      } catch (e) {
        // Silent fail - no error shown
        console.log('[Admin] Access check failed');
      }
    }
  };

  const handleSkipGuide = () => {
    setShowGuideReminder(false);
    if (pendingTab) {
      setActiveTab(pendingTab);
      setPendingTab(null);
    }
    setSidebarOpen(false);
  };

  // Skip guide permanently - mark as complete and don't show reminder again
  const handleSkipGuidePermanently = async () => {
    setShowGuideReminder(false);
    setGuideComplete(true);
    localStorage.setItem('fira-quiz-complete', 'true');
    
    // Save to backend
    try {
      await saveGuideProgress({
        completed_sections: ['skipped'],
        skipped: true,
        skipped_at: new Date().toISOString()
      });
    } catch (error) {
      console.error('Error saving guide skip:', error);
    }
    
    if (pendingTab) {
      setActiveTab(pendingTab);
      setPendingTab(null);
    }
    setSidebarOpen(false);
  };

  const handleBackToGuide = () => {
    setShowGuideReminder(false);
    setPendingTab(null);
  };

  // If auth is still loading, show loading spinner
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-amber-500/30 border-t-amber-500 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-300">Loading...</p>
        </div>
      </div>
    );
  }

  // If not authenticated, show login page
  if (!isAuthenticated) {
    return <AuthPage />;
  }

  if (loading) {
    return (
      <div className="app-container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
        <div style={{ textAlign: 'center', padding: '20px', width: '100%', maxWidth: '400px' }}>
          <div style={{ 
            width: '50px', 
            height: '50px', 
            border: '3px solid var(--border-subtle)', 
            borderTop: '3px solid #E01E3A', 
            borderRadius: '50%', 
            animation: 'spin 1s linear infinite', 
            margin: '0 auto 20px' 
          }} />
          <div style={{ color: '#E01E3A', fontWeight: '600', marginBottom: '20px' }}>Loading MSE SUPREME AI...</div>
          <StatsSkeleton />
        </div>
      </div>
    );
  }
  
  // Show Admin Panel if activated (with lazy loading)
  if (showAdminPanel) {
    return (
      <Suspense fallback={
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: '#0a0a0a' }}>
          <div style={{ width: '30px', height: '30px', border: '3px solid rgba(224,30,58,0.2)', borderTopColor: '#E01E3A', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
          <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '13px', marginTop: '12px' }}>Loading Admin Panel...</p>
        </div>
      }>
        <AdminPanel onBack={() => setShowAdminPanel(false)} />
      </Suspense>
    );
  }

  if (!isActivated) {
    return (
      <div className="app-container">
        <PreActivation onActivate={handleActivate} />
        {/* WhatsApp Float removed - no function */}
      </div>
    );
  }

  const tabs = [
    { id: 'checklist', labelKey: 'menu.checklist', icon: CheckSquare },
    { id: 'tools', labelKey: 'menu.tools', icon: Calculator },
    { id: 'premium', label: '⭐ Supreme Signal AI', icon: Crown, highlight: true },
    { id: 'education', labelKey: 'menu.education', icon: GraduationCap },
  ];

  return (
    <div className="app-container" style={{ display: 'flex' }}>
      {/* Desktop Sidebar - Always Visible */}
      {!isMobile && (
        <div 
          className="desktop-sidebar"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '260px',
            height: '100vh',
            background: 'var(--bg-card)',
            borderRight: '1px solid var(--border-subtle)',
            zIndex: 100,
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          {/* Sidebar Header */}
          <div style={{
            padding: '16px 18px',
            borderBottom: '1px solid var(--border-subtle)',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            cursor: 'pointer'
          }}
          onClick={handleLogoClick}
          data-testid="logo-click-area"
          >
            <img
              src="https://customer-assets.emergentagent.com/job_b546c7e0-8db7-4773-8c43-bb6a44e7d135/artifacts/0qixik5j_IMG_2358.jpeg"
              alt="MSE Logo"
              style={{ width: '38px', height: '38px', borderRadius: '10px', border: '2px solid #E01E3A' }}
            />
            <div>
              <div style={{ fontWeight: '700', fontSize: '15px', color: '#E01E3A' }}>MSE SUPREME AI</div>
              <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>Multi Setup Entry</div>
            </div>
          </div>

          {/* Menu Items */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '10px' }}>
            {tabs.map((tab) => {
              const isPremiumItem = tab.id === 'premium';
              const isActive = activeTab === tab.id;
              const isLocked = tab.locked;
              
              return (
                <button
                  key={tab.id}
                  onClick={() => !isLocked && handleTabChange(tab.id)}
                  data-testid={`menu-${tab.id}`}
                  disabled={isLocked}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: isPremiumItem ? '14px 14px' : '12px 14px',
                    marginBottom: isPremiumItem ? '6px' : '3px',
                    marginTop: isPremiumItem ? '6px' : '0',
                    background: isLocked 
                      ? 'rgba(100, 100, 100, 0.1)'
                      : isPremiumItem 
                        ? 'linear-gradient(135deg, #E01E3A 0%, #D41A34 50%, #8B1028 100%)'
                        : isActive 
                          ? '#E01E3A'
                          : 'transparent',
                    border: isLocked ? '1px dashed var(--border-subtle)' : 'none',
                    borderRadius: isPremiumItem ? '12px' : '10px',
                    cursor: isLocked ? 'not-allowed' : 'pointer',
                    color: isLocked 
                      ? 'var(--text-muted)' 
                      : isPremiumItem ? '#FFFFFF' : isActive ? '#FFFFFF' : 'var(--text-primary)',
                    fontSize: '14px',
                    fontWeight: isPremiumItem ? '700' : isActive ? '600' : '500',
                    transition: 'all 0.2s ease',
                    textAlign: 'left',
                    boxShadow: isPremiumItem 
                      ? '0 4px 15px rgba(224, 30, 58, 0.4), 0 0 15px rgba(224, 30, 58, 0.2)' 
                      : 'none',
                    position: 'relative',
                    overflow: 'hidden',
                    opacity: isLocked ? 0.6 : 1
                  }}
                >
                  {/* Shimmer effect for Premium */}
                  {isPremiumItem && (
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      left: '-100%',
                      width: '100%',
                      height: '100%',
                      background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
                      animation: 'premiumShimmer 2s infinite'
                    }} />
                  )}
                  <tab.icon size={18} style={{ 
                    position: 'relative', 
                    zIndex: 1,
                    filter: isPremiumItem ? 'drop-shadow(0 0 3px rgba(255,255,255,0.5))' : 'none',
                    opacity: isLocked ? 0.5 : 1
                  }} />
                  <span style={{ position: 'relative', zIndex: 1, flex: 1 }}>
                    {isPremiumItem ? '⭐ Supreme Signal AI' : (tab.label || t(tab.labelKey))}
                  </span>
                  {/* Locked Badge */}
                  {tab.badge && (
                    <span style={{
                      background: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
                      padding: '2px 8px',
                      borderRadius: '10px',
                      fontSize: '9px',
                      fontWeight: '700',
                      letterSpacing: '0.5px',
                      position: 'relative',
                      zIndex: 1,
                      color: '#fff',
                      textTransform: 'uppercase'
                    }}>
                      {tab.badge}
                    </span>
                  )}
                  {isPremiumItem && (
                    <span style={{
                      background: 'rgba(255,255,255,0.25)',
                      padding: '2px 6px',
                      borderRadius: '8px',
                      fontSize: '8px',
                      fontWeight: '700',
                      letterSpacing: '0.5px',
                      position: 'relative',
                      zIndex: 1
                    }}>
                      VIP
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Sidebar Footer */}
          <div style={{
            padding: '14px 16px',
            borderTop: '1px solid var(--border-subtle)',
          }}>
            {/* User Info */}
            {user && (
              <div style={{ marginBottom: '12px', textAlign: 'center' }}>
                <div style={{ fontSize: '13px', color: 'var(--text-secondary)', fontWeight: '500' }}>
                  {user.name}
                </div>
                <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                  {user.email}
                </div>
              </div>
            )}
            {/* Logout Button */}
            <button
              onClick={logout}
              data-testid="logout-btn-desktop"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                width: '100%',
                padding: '10px',
                background: 'rgba(239, 68, 68, 0.1)',
                border: '1px solid rgba(239, 68, 68, 0.3)',
                borderRadius: '8px',
                color: '#ef4444',
                fontSize: '13px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                marginBottom: '8px'
              }}
            >
              <LogOut size={16} />
              {language === 'bm' || language === 'ms' ? 'Log Keluar' : 'Logout'}
            </button>
            <div style={{
              fontSize: '11px',
              color: 'var(--text-muted)',
              textAlign: 'center'
            }}>
              {t('footer.tagline')}
            </div>
          </div>
        </div>
      )}

      {/* Main Content Area */}
      <div style={{ 
        marginLeft: isMobile ? 0 : '260px', 
        width: isMobile ? '100%' : 'calc(100% - 260px)',
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}>
        {/* Live Price Ticker - DISABLED (saves server resources for 500 concurrent users) */}
        {/* <LivePriceTicker symbols={['XAU/USD', 'EUR/USD', 'GBP/USD', 'USD/JPY']} /> */}
        
        {/* Header Bar - ALWAYS VISIBLE */}
        <header 
          style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px',
            position: 'sticky',
            top: 0,
            left: 0,
            right: 0,
            width: '100%',
            maxWidth: '100vw',
            height: '56px',
            padding: '8px 12px',
            background: 'linear-gradient(135deg, #E01E3A 0%, #C41830 100%)',
            zIndex: 99,
            boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
            boxSizing: 'border-box'
          }}
        >
          {/* MSE Logo - Admin Panel access (7 clicks) */}
          {isMobile && (
            <div 
              onClick={handleLogoClick}
              data-testid="logo-click-area-mobile"
              style={{ cursor: 'pointer', flexShrink: 0 }}
            >
              <img
                src="https://customer-assets.emergentagent.com/job_b546c7e0-8db7-4773-8c43-bb6a44e7d135/artifacts/0qixik5j_IMG_2358.jpeg"
                alt="MSE Logo"
                style={{ width: '36px', height: '36px', borderRadius: '8px', border: '2px solid rgba(255,255,255,0.4)' }}
              />
            </div>
          )}

          {/* Exchange Rate */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            padding: '6px 12px',
            background: 'rgba(255, 255, 255, 0.25)',
            borderRadius: '20px'
          }}>
            <span style={{ fontSize: '8px', color: 'rgba(255, 255, 255, 0.9)', fontWeight: '500' }}>USD / MYR</span>
            <span style={{ fontSize: '12px', fontWeight: '700', color: '#FFFFFF' }}>RM {exchangeRate.toFixed(2)}</span>
          </div>
          
          {/* Spacer */}
          <div style={{ flex: 1 }} />
          
          {/* Refresh Button */}
          <button
            onClick={() => {
              if ('caches' in window) caches.keys().then(n => n.forEach(name => caches.delete(name)));
              window.location.reload();
            }}
            data-testid="refresh-button"
            style={{
              background: 'rgba(255, 255, 255, 0.25)',
              border: 'none',
              color: '#FFFFFF',
              padding: '10px 14px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '20px'
            }}
          >
            <RefreshCw size={18} />
          </button>
          
          {/* Language Toggle */}
          <button
            onClick={() => {
              const current = localStorage.getItem('fira-language') || 'bm';
              localStorage.setItem('fira-language', current === 'bm' ? 'en' : 'bm');
              localStorage.setItem('fira-active-tab', activeTab);
              window.location.reload();
            }}
            data-testid="language-toggle"
            style={{
              background: 'rgba(255, 255, 255, 0.25)',
              border: 'none',
              color: '#FFFFFF',
              padding: '8px 12px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              borderRadius: '20px',
              fontSize: '11px',
              fontWeight: '600'
            }}
          >
            <Globe size={14} />
            {(localStorage.getItem('fira-language') || 'bm') === 'bm' ? '🇲🇾 BM' : '🇬🇧 EN'}
          </button>
        </header>
        
        <main className="main-content" style={{ paddingBottom: isMobile ? '80px' : '20px', flex: 1 }}>
          <div className="dashboard-container">

            {/* Daily Checklist Tab */}
            {activeTab === 'checklist' && (
              <Suspense fallback={<TabFallback />}>
              <div className="dashboard-grid">
                <div style={{ gridColumn: '1 / -1' }}>
                  <DailyChecklist key={refreshKey} onComplete={() => {}} />
                </div>
              </div>
              </Suspense>
            )}

          {/* Premium Features Tab (with lazy loading) */}
          {activeTab === 'premium' && (
            <Suspense fallback={<TabFallback />}>
              <div className="dashboard-grid">
                <PremiumFeatures key={refreshKey} />
              </div>
            </Suspense>
          )}

          {/* Tools Tab - Combined Tools & Calculators */}
          {activeTab === 'tools' && (
            <Suspense fallback={<TabFallback />}>
            <div className="dashboard-grid" key={refreshKey}>
              {/* Risk & Position Tools */}
              <RiskCalculator exchangeRate={exchangeRate} />
              <TargetTracker exchangeRate={exchangeRate} />
              <DrawdownAlert />
              
              {/* Calculators */}
              <PipValueCalculator exchangeRate={exchangeRate} />
              <CompoundingCalculator exchangeRate={exchangeRate} />
              <BreakEvenCalculator />
              
              {/* Market Tools */}
              <MarketSnapshot />
              <EconomicCalendar />
            </div>
            </Suspense>
          )}


          {/* Education Tab */}
          {activeTab === 'education' && (
            <Suspense fallback={<TabFallback />}>
            <div className="dashboard-grid" key={refreshKey}>
              <div style={{ gridColumn: '1 / -1' }}>
                <TradingEducation />
              </div>
            </div>
            </Suspense>
          )}

          {/* Fallback for invalid tabs - redirect to premium */}
          {!['checklist', 'tools', 'premium', 'education'].includes(activeTab) && (
            <div className="dashboard-grid" key={refreshKey}>
              <div className="module-card" style={{ textAlign: 'center', padding: '40px 20px' }}>
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔄</div>
                <h3 style={{ color: 'var(--text-primary)', marginBottom: '8px' }}>Loading...</h3>
                <p style={{ color: 'var(--text-muted)', fontSize: '14px', marginBottom: '20px' }}>
                  Redirecting...
                </p>
                <button 
                  onClick={() => setActiveTab('premium')}
                  style={{
                    padding: '12px 24px',
                    background: 'linear-gradient(135deg, #E01E3A, #C41830)',
                    border: 'none',
                    borderRadius: '8px',
                    color: '#fff',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Go to Supreme Signal AI
                </button>
              </div>
            </div>
          )}
        </div>
      </main>
      </div>
      
      {/* Bottom Navigation - Mobile Only */}
      {isMobile && (
        <BottomNav 
          activeTab={activeTab} 
          onTabChange={handleTabChange}
        />
      )}
    </div>
  );
}

function App() {
  const [showSystemGuideGlobal, setShowSystemGuideGlobal] = useState(false);
  
  // Global event listener for system guide
  useEffect(() => {
    const handleShowSystemGuide = () => setShowSystemGuideGlobal(true);
    window.addEventListener('showSystemGuide', handleShowSystemGuide);
    return () => window.removeEventListener('showSystemGuide', handleShowSystemGuide);
  }, []);
  
  return (
    <BrowserRouter>
      <ErrorBoundary>
      <AuthProvider>
        <ThemeProvider>
          <LanguageProvider>
            <ToastProvider>
              <Routes>
                {/* Direct to Login - Skip Landing Page for better UX */}
                <Route path="/" element={<AuthPageWrapper />} />
                
                {/* Login/Register Page (alias) */}
                <Route path="/login" element={<AuthPageWrapper />} />
                
                {/* Password Reset Page */}
                <Route path="/reset-password" element={<AuthPageWrapper />} />
                
                {/* Protected Dashboard */}
                <Route path="/dashboard/*" element={<AppContent />} />
                
                {/* Redirect all unknown routes to login */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
              
              {/* Global Live Support - Only on dashboard */}
              <SystemGuideModal 
                isOpen={showSystemGuideGlobal} 
                onClose={() => setShowSystemGuideGlobal(false)} 
              />
            </ToastProvider>
          </LanguageProvider>
        </ThemeProvider>
      </AuthProvider>
      </ErrorBoundary>
    </BrowserRouter>
  );
}

// Auth Page Wrapper - redirect if already logged in
function AuthPageWrapper() {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return <LazyLoadFallback />;
  }
  
  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return <AuthPage />;
}

export default App;
